// Formulário de convite (conta ou empresa).
import { useState, type FormEvent } from 'react'
import { UserPlus } from 'lucide-react'
import type { AccountRole, CompanyRole } from '@impulso/shared'
import type { CreateInvitationRequest } from '@impulso/shared/api/tenancy'
import { api, errorMessage } from '../../lib/api'
import { FormError } from '../auth/AuthShell'
import { ACCOUNT_ROLE_HELP, ACCOUNT_ROLE_LABEL, COMPANY_ROLE_HELP, COMPANY_ROLE_LABEL } from './labels'
import UpgradeHint, { isPlanLimit } from './UpgradeHint'

type Props =
  | { mode: 'account'; accountId: string; isOwner: boolean; canBill: boolean; onDone: () => void }
  | { mode: 'company'; accountId: string; companyId: string; canInviteClient: boolean; canBill: boolean; onDone: () => void }

export default function InviteForm(props: Props) {
  const [email, setEmail] = useState('')
  const [accountRole, setAccountRole] = useState<AccountRole>('admin')
  const [companyRole, setCompanyRole] = useState<CompanyRole>('manager')
  const [clientCanApprove, setClientCanApprove] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const accountRoles: AccountRole[] = props.mode === 'account' && props.isOwner ? ['admin', 'member', 'owner'] : ['admin', 'member']
  const companyRoles: CompanyRole[] = props.mode === 'company' && props.canInviteClient ? ['manager', 'analyst', 'viewer', 'client'] : ['manager', 'analyst', 'viewer']

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setOk(null)
    const body: CreateInvitationRequest = props.mode === 'account'
      ? { email, accountRole }
      : { email, companyId: props.companyId, companyRole, clientCanApprove: companyRole === 'client' ? clientCanApprove : undefined }
    setBusy(true)
    try {
      await api.post(`/accounts/${props.accountId}/invitations`, body)
      setOk(`Convite enviado para ${email}. O link vale por 7 dias.`)
      setEmail('')
      props.onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="stack" style={{ gap: 12 }} onSubmit={submit}>
      {error != null && (isPlanLimit(error)
        ? <UpgradeHint accountId={props.accountId} message={errorMessage(error)} canBill={props.canBill} />
        : <FormError error={errorMessage(error)} />)}
      {ok && <div className="form-ok" role="status">{ok}</div>}
      <div className="field">
        <label htmlFor="inv-email">E-mail da pessoa</label>
        <input id="inv-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="nome@exemplo.com" />
      </div>
      {props.mode === 'account' ? (
        <div className="field">
          <label htmlFor="inv-arole">Papel na conta</label>
          <select id="inv-arole" className="select" value={accountRole} onChange={(e) => setAccountRole(e.target.value as AccountRole)}>
            {accountRoles.map((r) => <option key={r} value={r}>{ACCOUNT_ROLE_LABEL[r]}</option>)}
          </select>
          <span className="small muted">{ACCOUNT_ROLE_HELP[accountRole]}</span>
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="inv-crole">O que a pessoa pode fazer nesta empresa</label>
            <select id="inv-crole" className="select" value={companyRole} onChange={(e) => setCompanyRole(e.target.value as CompanyRole)}>
              {companyRoles.map((r) => <option key={r} value={r}>{COMPANY_ROLE_LABEL[r]}</option>)}
            </select>
            <span className="small muted">{COMPANY_ROLE_HELP[companyRole]}</span>
          </div>
          {companyRole === 'client' && (
            <label className="row small"><input type="checkbox" checked={clientCanApprove} onChange={(e) => setClientCanApprove(e.target.checked)} /> O cliente pode aprovar sugestões</label>
          )}
        </>
      )}
      <div className="form-actions">
        <button className="btn primary" disabled={busy}><UserPlus size={15} /> {busy ? 'Enviando…' : 'Enviar convite'}</button>
      </div>
    </form>
  )
}
